import { useEffect, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Stack } from '../atoms/Stack/Stack';
import { Grid } from '../atoms/Grid/Grid';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { Section } from '../templates/Section/Section';
import { useTranslation } from '../../i18n/useTranslation';
import { ApiError } from '../../lib/apiClient';
import { refreshSession } from '../../lib/sessionRefresh';
import { PASSWORD_MAX_LENGTH } from '../../lib/authValidation';
import {
  changeOwnPassword,
  fetchOwnProfile,
  INVALID_PASSWORD,
  meetsPasswordPolicy,
  RATE_LIMITED,
  updateOwnProfile,
  WRONG_PASSWORD,
  type OwnProfile,
} from '../../services/accountService';

export const OWN_PROFILE_QUERY_KEY = ['me', 'profile'] as const;
const NAME_MAX = 100;

export interface AccountSectionProps {
  className?: string;
}

/**
 * Settings → Account: the e-mail address (read-only), first/last name
 * (PATCH /api/v1/me/profile — users row + Cognito attributes) and the password
 * change (POST /api/v1/me/password — Cognito ChangePassword). Field errors are
 * the Input atom's `error` text; save errors are an Alert; success is
 * announced in a polite live region.
 */
export function AccountSection({ className = '' }: AccountSectionProps) {
  const { t } = useTranslation();
  const profile = useQuery({
    queryKey: OWN_PROFILE_QUERY_KEY,
    queryFn: fetchOwnProfile,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  let content;
  if (profile.isPending) {
    content = (
      <LiveRegion busy>
        <Text size="sm" color="muted">{t('settings.account.loading')}</Text>
      </LiveRegion>
    );
  } else if (profile.isError || profile.data === undefined) {
    content = (
      <LoadErrorState
        compact
        titleLevel={3}
        title={t('settings.account.loadError')}
        onRetry={() => void profile.refetch()}
        retryLabel={t('settings.account.retry')}
        retrying={profile.isFetching}
      />
    );
  } else {
    content = (
      <Stack gap="8">
        <ProfileForm profile={profile.data} />
        <PasswordForm />
      </Stack>
    );
  }

  return (
    <Section
      title={t('settings.account.title')}
      description={t('settings.account.description')}
      className={className || undefined}
    >
      {content}
    </Section>
  );
}

// ── Profile ─────────────────────────────────────────────────────────────────

interface NameErrors {
  firstName?: string;
  lastName?: string;
}

function ProfileForm({ profile }: { profile: OwnProfile }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState(profile.firstName);
  const [lastName, setLastName] = useState(profile.lastName);
  const [errors, setErrors] = useState<NameErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFirstName(profile.firstName);
    setLastName(profile.lastName);
  }, [profile.firstName, profile.lastName]);

  const validateName = (value: string, requiredKey: string): string | undefined => {
    const trimmed = value.trim();
    if (trimmed === '') return t(requiredKey);
    if (trimmed.length > NAME_MAX) return t('settings.account.profile.errors.tooLong', { max: NAME_MAX });
    return undefined;
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (saving) return;
    setSaved(false);
    setSaveError(null);
    const next: NameErrors = {
      firstName: validateName(firstName, 'settings.account.profile.errors.firstNameRequired'),
      lastName: validateName(lastName, 'settings.account.profile.errors.lastNameRequired'),
    };
    setErrors(next);
    if (next.firstName || next.lastName) return;

    setSaving(true);
    try {
      const updated = await updateOwnProfile({ firstName: firstName.trim(), lastName: lastName.trim() });
      queryClient.setQueryData(OWN_PROFILE_QUERY_KEY, updated);
      setSaved(true);
      // New ID token with the new name (best effort — the next refresh does it anyway).
      void refreshSession().catch(() => undefined);
    } catch (err: unknown) {
      setSaveError(
        err instanceof ApiError && err.code === RATE_LIMITED
          ? t('settings.account.profile.errors.rateLimited')
          : t('settings.account.profile.errors.generic'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      <Stack gap="4">
        <Heading level={3}>{t('settings.account.profile.title')}</Heading>
        <Input
          id="account-email"
          type="email"
          label={t('settings.account.profile.email')}
          helperText={t('settings.account.profile.emailHelp')}
          value={profile.email}
          readOnly
          fullWidth
        />
        <Grid minItemWidth="sm" gap="4">
          <Input
            id="account-first-name"
            label={t('settings.account.profile.firstName')}
            autoComplete="given-name"
            value={firstName}
            error={errors.firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              setErrors((prev) => ({ ...prev, firstName: undefined }));
              setSaved(false);
            }}
            maxLength={NAME_MAX}
            required
            fullWidth
            disabled={saving}
          />
          <Input
            id="account-last-name"
            label={t('settings.account.profile.lastName')}
            autoComplete="family-name"
            value={lastName}
            error={errors.lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              setErrors((prev) => ({ ...prev, lastName: undefined }));
              setSaved(false);
            }}
            maxLength={NAME_MAX}
            required
            fullWidth
            disabled={saving}
          />
        </Grid>
        {saveError && <Alert>{saveError}</Alert>}
        <Stack gap="3" align="start">
          <Button type="submit" variant="primary" isLoading={saving}>
            {t('settings.account.profile.save')}
          </Button>
        </Stack>
        <LiveRegion>
          {saved ? (
            <Alert tone="success" live="off" compact>
              {t('settings.account.profile.saved')}
            </Alert>
          ) : null}
        </LiveRegion>
      </Stack>
    </form>
  );
}

// ── Password ────────────────────────────────────────────────────────────────

interface PasswordErrors {
  current?: string;
  next?: string;
  confirm?: string;
}

function PasswordForm() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<PasswordErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [changed, setChanged] = useState(false);
  const [saving, setSaving] = useState(false);

  const clear = (field: keyof PasswordErrors): void => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    setChanged(false);
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (saving) return;
    setChanged(false);
    setSubmitError(null);
    const found: PasswordErrors = {
      current:
        current === ''
          ? t('settings.account.password.errors.currentRequired')
          : current.length > PASSWORD_MAX_LENGTH
            ? t('auth.passwordTooLong')
            : undefined,
      next:
        next.length > PASSWORD_MAX_LENGTH
          ? t('auth.passwordTooLong')
          : !meetsPasswordPolicy(next)
            ? t('settings.account.password.errors.policy')
            : undefined,
      confirm: confirm !== next || confirm === '' ? t('settings.account.password.errors.mismatch') : undefined,
    };
    setErrors(found);
    if (found.current || found.next || found.confirm) return;

    setSaving(true);
    try {
      await changeOwnPassword(current, next);
      setCurrent('');
      setNext('');
      setConfirm('');
      setChanged(true);
    } catch (err: unknown) {
      const code = err instanceof ApiError ? err.code : '';
      if (code === WRONG_PASSWORD) {
        setErrors({ current: t('settings.account.password.errors.wrongCurrent') });
      } else if (code === INVALID_PASSWORD || code === 'VALIDATION_ERROR') {
        setErrors({ next: t('settings.account.password.errors.policy') });
      } else if (code === RATE_LIMITED) {
        setSubmitError(t('settings.account.password.errors.rateLimited'));
      } else {
        setSubmitError(t('settings.account.password.errors.generic'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      <Stack gap="4">
        <Heading level={3}>{t('settings.account.password.title')}</Heading>
        <Input
          id="account-current-password"
          type="password"
          autoComplete="current-password"
          label={t('settings.account.password.current')}
          value={current}
          error={errors.current}
          onChange={(e) => {
            setCurrent(e.target.value);
            clear('current');
          }}
          required
          fullWidth
          disabled={saving}
        />
        <Input
          id="account-new-password"
          type="password"
          autoComplete="new-password"
          label={t('settings.account.password.new')}
          helperText={t('settings.account.password.policy')}
          value={next}
          error={errors.next}
          onChange={(e) => {
            setNext(e.target.value);
            clear('next');
          }}
          required
          fullWidth
          disabled={saving}
        />
        <Input
          id="account-confirm-password"
          type="password"
          autoComplete="new-password"
          label={t('settings.account.password.confirm')}
          value={confirm}
          error={errors.confirm}
          onChange={(e) => {
            setConfirm(e.target.value);
            clear('confirm');
          }}
          required
          fullWidth
          disabled={saving}
        />
        {submitError && <Alert>{submitError}</Alert>}
        <Stack gap="3" align="start">
          <Button type="submit" variant="secondary" isLoading={saving}>
            {t('settings.account.password.submit')}
          </Button>
        </Stack>
        <LiveRegion>
          {changed ? (
            <Alert tone="success" live="off" compact>
              {t('settings.account.password.changed')}
            </Alert>
          ) : null}
        </LiveRegion>
      </Stack>
    </form>
  );
}

export default AccountSection;
